import { useState } from "react";
import {
  CheckinAnswers,
  FeelAnswer,
  PainAnswer,
  PainArea,
  PAIN_AREAS,
  PAIN_QUESTION,
  Question,
  SLEEP_QUESTION,
  SleepAnswer,
  FEEL_QUESTION,
} from "../lib/checkin";
import { ErrorState } from "./Loading";

/**
 * Three quick taps before every workout: sleep, how they feel, anything hurting (decided 2026-10-04).
 *
 * There's no skip. The questions are asked every time so the coach can see a trend, and a check-in
 * that can be skipped gets skipped by exactly the athlete it would have flagged.
 *
 * "Something hurts" changes what's offered. The athlete can still train, going easy, or not train
 * today. Either way the coach sees it on the athlete's page. Nothing is sent as a notification (also
 * decided 2026-10-04), so the copy here doesn't promise one.
 */
export function CheckIn({
  athleteName,
  onTrain,
  onRest,
  sending,
  error,
}: {
  athleteName?: string;
  onTrain: (answers: CheckinAnswers) => void;
  onRest: (answers: CheckinAnswers) => void;
  sending: boolean;
  error: unknown;
}) {
  const [sleep, setSleep] = useState<SleepAnswer | null>(null);
  const [feel, setFeel] = useState<FeelAnswer | null>(null);
  const [pain, setPain] = useState<PainAnswer | null>(null);
  const [areas, setAreas] = useState<PainArea[]>([]);
  const [note, setNote] = useState("");

  const hurts = pain === "yes";
  const answered = sleep !== null && feel !== null && pain !== null;
  const firstName = athleteName?.trim().split(/\s+/)[0];

  function answers(): CheckinAnswers {
    return {
      sleep: sleep!,
      feel: feel!,
      pain: pain!,
      // Only what's true of the final answer: picking the knee and then answering "No" isn't a knee.
      painAreas: hurts ? areas : [],
      painNote: hurts ? note.trim() : "",
    };
  }

  function toggleArea(area: PainArea) {
    setAreas((current) => (current.includes(area) ? current.filter((a) => a !== area) : [...current, area]));
  }

  return (
    <div className="walkthrough-card checkin">
      <p className="stage-progress">Before you start · 3 quick taps</p>
      <h2>How are you feeling today{firstName ? `, ${firstName}` : ""}?</h2>
      <p className="form-hint">Your coach sees your answers with today&rsquo;s workout.</p>

      <ChoiceQuestion question={SLEEP_QUESTION} value={sleep} onChange={setSleep} />
      <ChoiceQuestion question={FEEL_QUESTION} value={feel} onChange={setFeel} />
      <ChoiceQuestion question={PAIN_QUESTION} value={pain} onChange={setPain} />

      {hurts && (
        <div className="checkin-hurt">
          <p className="checkin-question" id="checkin-where">
            Where does it hurt?
          </p>
          <div className="filter-chips" role="group" aria-labelledby="checkin-where">
            {PAIN_AREAS.map((area) => (
              <button
                key={area.value}
                type="button"
                className={areas.includes(area.value) ? "chip active" : "chip"}
                aria-pressed={areas.includes(area.value)}
                onClick={() => toggleArea(area.value)}
              >
                {area.text}
              </button>
            ))}
          </div>
          <label htmlFor="checkin-note">Add a note for your coach (optional)</label>
          <textarea
            id="checkin-note"
            rows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="e.g. right knee aches when I land"
          />
          <p className="checkin-warning">
            Your coach will see this next time they look at your page. If you train, go easy and stop if it gets
            worse &mdash; any exercise can be skipped.
          </p>
        </div>
      )}

      {error ? <ErrorState error={error} /> : null}

      <div className="checkin-actions">
        {hurts && (
          <button type="button" className="chip checkin-rest" disabled={sending} onClick={() => onRest(answers())}>
            Don&rsquo;t train today
          </button>
        )}
        <button type="button" disabled={!answered || sending} onClick={() => onTrain(answers())}>
          {sending ? "Saving…" : hurts ? "Train anyway, and go easy" : "Start workout"}
        </button>
        {!answered && <p className="form-hint">Answer all three to start.</p>}
      </div>
    </div>
  );
}

function ChoiceQuestion<T extends string>({
  question,
  value,
  onChange,
}: {
  question: Question<T>;
  value: T | null;
  onChange: (value: T) => void;
}) {
  const labelId = `checkin-${question.key}`;
  return (
    <div className="checkin-block">
      <p className="checkin-question" id={labelId}>
        {question.label}
      </p>
      <div className="checkin-options" role="radiogroup" aria-labelledby={labelId}>
        {question.options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            className={value === option.value ? "checkin-option active" : "checkin-option"}
            onClick={() => onChange(option.value)}
          >
            {option.text}
          </button>
        ))}
      </div>
    </div>
  );
}
